import axios from './AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';

/**
 * BankConnectionService — API client for PSD2 bank connections
 * (Settings → Bankanslutningar). Backend routes live under
 * /:companyId/accounting/reconciliation/banking/connections; the SCA
 * callback round-trip mirrors the E-postinkorg OAuth flow (full-page
 * redirect to the bank, backend 302s back with ?bank_connected=…).
 */

const base = (companyId) => `${companyId}/accounting/reconciliation/banking/connections`;

/** GET the connectable banks for the picker (business accounts only). */
export async function fetchBanks(companyId, country = 'SE') {
    const { data } = await axios.get(getApiUrl(`${base(companyId)}/banks?country=${country}`), {
        withCredentials: true,
    });
    return data; // { configured, banks: [{ name, country, logoUrl, maxConsentSeconds }] }
}

/** GET all connections incl. their consented accounts. */
export async function fetchConnections(companyId) {
    const { data } = await axios.get(getApiUrl(base(companyId)), { withCredentials: true });
    return data.connections || [];
}

/** POST authorize — returns { authorizationUrl } for the full-page redirect. */
export async function startConnect(companyId, bankName, bankCountry = 'SE') {
    const { data } = await axios.post(
        getApiUrl(`${base(companyId)}/authorize`),
        { bankName, bankCountry },
        { withCredentials: true }
    );
    return data;
}

/** POST user-present sync ("Uppdatera nu") — doesn't count against the PSD2 cap. */
export async function syncNow(companyId, connectionId) {
    const { data } = await axios.post(
        getApiUrl(`${base(companyId)}/${connectionId}/sync`),
        {},
        { withCredentials: true, timeout: 120000 }
    );
    return data;
}

/** PATCH per-account sync opt-in/out. */
export async function setAccountEnabled(companyId, connectionAccountId, enabled) {
    return patchFeedAccount(companyId, connectionAccountId, { enabled });
}

/** Generic per-account PATCH: { enabled? , account_number?, clearing_number? } (mapping link). */
export async function patchFeedAccount(companyId, connectionAccountId, body) {
    const { data } = await axios.patch(
        getApiUrl(`${base(companyId)}/accounts/${connectionAccountId}`),
        body,
        { withCredentials: true }
    );
    return data;
}

/** DELETE — revokes the consent at the bank and disconnects locally. */
export async function disconnect(companyId, connectionId) {
    const { data } = await axios.delete(getApiUrl(`${base(companyId)}/${connectionId}`), {
        withCredentials: true,
    });
    return data;
}
